<template>
    <div class="fac-chart">
        <div class="fac-chart__header">
            <div>
                <p class="fac-chart__eyebrow">Section ranking</p>
                <h3 class="fac-chart__title">Top facility sections</h3>
            </div>
        </div>
        <div v-if="!hasData" class="fac-chart__empty">No section counts available yet.</div>
        <apexchart
            v-else
            type="bar"
            height="320"
            :options="chartOptions"
            :series="series"
            @dataPointSelection="onBarSelect"
        />
    </div>
</template>

<script>
export default {
    name: "FacilityTopSectionsChart",
    props: {
        data: {
            type: Array,
            default: function () {
                return [];
            },
        },
    },
    emits: ["select"],
    data() {
        return {
            series: [],
            activeRows: [],
            chartOptions: {
                chart: {
                    type: "bar",
                    fontFamily: "inherit",
                    toolbar: { show: false },
                    animations: { enabled: true, easing: "easeinout", speed: 450 },
                },
                colors: [
                    "#b45309",
                    "#c2410c",
                    "#d97706",
                    "#0f766e",
                    "#57534e",
                    "#ca8a04",
                    "#78716c",
                    "#92400e",
                ],
                plotOptions: {
                    bar: {
                        horizontal: true,
                        borderRadius: 6,
                        borderRadiusApplication: "end",
                        barHeight: "58%",
                        distributed: true,
                    },
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: "11px",
                        fontWeight: 700,
                        colors: ["#1c1917"],
                    },
                    offsetX: 4,
                },
                legend: { show: false },
                grid: {
                    borderColor: "#f5f5f4",
                    strokeDashArray: 3,
                    xaxis: { lines: { show: true } },
                    yaxis: { lines: { show: false } },
                    padding: { left: 8, right: 12 },
                },
                xaxis: {
                    categories: [],
                    labels: {
                        style: { colors: "#78716c", fontSize: "11px" },
                    },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
                yaxis: {
                    labels: {
                        style: {
                            colors: "#44403c",
                            fontSize: "12px",
                            fontWeight: 600,
                        },
                        maxWidth: 120,
                    },
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " records";
                        },
                    },
                },
                states: {
                    hover: {
                        filter: { type: "lighten", value: 0.08 },
                    },
                    active: {
                        allowMultipleDataPointsSelection: false,
                        filter: { type: "darken", value: 0.15 },
                    },
                },
            },
        };
    },
    computed: {
        hasData() {
            return (
                Array.isArray(this.data) &&
                this.data.some(function (item) {
                    return item && item.count > 0;
                })
            );
        },
    },
    watch: {
        data: {
            handler: function (rows) {
                if (!Array.isArray(rows)) return;
                var filtered = rows.filter(function (item) {
                    return item && item.count > 0;
                });
                this.activeRows = filtered;
                this.series = [
                    {
                        name: "Records",
                        data: filtered.map(function (item) {
                            return item.count;
                        }),
                    },
                ];
                this.chartOptions = Object.assign({}, this.chartOptions, {
                    xaxis: Object.assign({}, this.chartOptions.xaxis, {
                        categories: filtered.map(function (item) {
                            return item.label;
                        }),
                    }),
                });
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        onBarSelect(event, chartContext, config) {
            var index =
                config && typeof config.dataPointIndex === "number"
                    ? config.dataPointIndex
                    : -1;
            if (index < 0 || index >= this.activeRows.length) return;

            var row = this.activeRows[index];
            if (!row || !row.key) return;

            this.$emit("select", {
                key: row.key,
                label: row.label,
                count: row.count,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.fac-chart {
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 1rem;
    padding: 1.25rem 1.35rem 1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    height: 100%;
}

.fac-chart__eyebrow {
    margin: 0;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
}

.fac-chart__title {
    margin: 0.2rem 0 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #1c1917;
}

.fac-chart__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 16rem;
    color: #a8a29e;
    font-size: 0.9rem;
}

.fac-chart :deep(.apexcharts-bar-area) {
    cursor: pointer;
}
</style>
